import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import { motion } from "framer-motion";
import { TarotCard, tarotCards } from "../../utils/tarotCards";
import {
  checkDailyLimit,
  incrementReadingCount,
  DailyLimitStatus,
} from "../../utils/dailyLimit";
import toast from "react-hot-toast";
import { Howl } from "howler";
import { useTranslation } from "react-i18next";
import AstriaCardBack from "./AstriaCardBack";

// Optional visual theme override for the question-input card, so callers with
// their own design system (e.g. JPTarotPage's ivory/red palette) can restyle
// it without affecting the default purple theme used elsewhere (TarotCardCategory).
export interface TarotFanSpreadTheme {
  cardBg: string;
  cardBorder: string;
  titleColor: string;
  titleFont: string;
  bodyFont: string;
  inputBorder: string;
  inputFocusRing: string;
  inputText: string;
  placeholderColor: string;
  buttonGradient: string;
  buttonShadow: string;
  mutedColor: string;
  borderRadius: number;
}

interface TarotFanSpreadProps {
  onGetReading: (cards: TarotCard[], question: string) => void;
  category: string;
  isLoading?: boolean;
  theme?: TarotFanSpreadTheme;
  useAstriaCardBack?: boolean;
}

type AnimationMode = "idle" | "shuffle" | "cut";

const fisherYatesShuffle = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const riffleShuffle = <T,>(array: T[]): T[] => {
  const mid = Math.floor(array.length / 2);
  const offset = Math.random() > 0.5 ? 1 : -1;
  const split = Math.max(1, Math.min(array.length - 1, mid + offset));
  const left = fisherYatesShuffle(array.slice(0, split));
  const right = fisherYatesShuffle(array.slice(split));
  const merged: T[] = [];

  let l = 0;
  let r = 0;
  while (l < left.length || r < right.length) {
    if (l < left.length) merged.push(left[l++]);
    if (r < right.length) merged.push(right[r++]);
  }

  return fisherYatesShuffle(merged);
};

const getPhase3TraversalOrder = (
  totalCards: number,
  useReverseTraversal: boolean,
): number[] => {
  if (totalCards <= 1) return [0];
  if (!useReverseTraversal) {
    return Array.from({ length: totalCards }, (_, i) => i);
  }
  return [
    0,
    ...Array.from({ length: totalCards - 1 }, (_, i) => totalCards - 1 - i),
  ];
};

// Move QuestionInput outside of the main component
const QuestionInput = memo(
  ({
    question,
    setQuestion,
    onSubmit,
    cardCount,
    theme,
  }: {
    question: string;
    setQuestion: (value: string) => void;
    onSubmit: () => void;
    cardCount: number;
    theme?: TarotFanSpreadTheme;
  }) => {
    // Add screen width state for responsive behavior
    const [screenWidth, setScreenWidth] = useState<number>(
      typeof window !== "undefined" ? window.innerWidth : 0,
    );
    const { t } = useTranslation();

    useEffect(() => {
      const updateWidth = () => setScreenWidth(window.innerWidth);
      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }, []);

    const isMobile = screenWidth < 640;

    if (theme) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="w-full max-w-2xl mx-auto mb-8 sm:mb-12 p-4 sm:p-6"
          style={{
            background: theme.cardBg,
            border: `1px solid ${theme.cardBorder}`,
            borderRadius: theme.borderRadius,
          }}
        >
          <h3
            className="text-base sm:text-lg mb-3 sm:mb-4 text-center px-2"
            style={{
              color: theme.titleColor,
              fontFamily: theme.titleFont,
              letterSpacing: "0.02em",
            }}
          >
            {t("tarotFanSpread.questionTitle")}
          </h3>

          <div className="relative">
            <style>{`#question-input.themed-tarot-input::placeholder { color: ${theme.placeholderColor}; opacity: 0.7; }`}</style>
            <textarea
              value={question}
              id="question-input"
              name="question"
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t("tarotFanSpread.questionPlaceholder")}
              rows={isMobile ? 2 : 3}
              className="themed-tarot-input w-full px-3 sm:px-4 py-2 sm:py-3 outline-none transition-colors resize-none text-sm sm:text-base"
              style={{
                borderRadius: Math.max(theme.borderRadius - 1, 3),
                border: `1px solid ${theme.inputBorder}`,
                backgroundColor: "#FFFFFF",
                color: theme.inputText,
                fontFamily: theme.bodyFont,
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = theme.inputFocusRing;
                e.currentTarget.style.boxShadow = `0 0 0 2px ${theme.inputFocusRing}33`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = theme.inputBorder;
                e.currentTarget.style.boxShadow = "none";
              }}
              autoFocus
            />

            <div className="flex justify-end mt-3 sm:mt-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onSubmit}
                className="px-6 sm:px-8 py-2 sm:py-3 rounded-full text-white font-medium transition-all duration-300 flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
                style={{
                  background: theme.buttonGradient,
                  boxShadow: theme.buttonShadow,
                  fontFamily: theme.bodyFont,
                }}
              >
                <span>{t("tarotFanSpread.questionButton")}</span>
                <span className="text-lg sm:text-xl">✨</span>
              </motion.button>
            </div>
          </div>

          <p
            className="text-[10px] sm:text-xs mt-2 sm:mt-3 text-center px-2"
            style={{ color: theme.mutedColor, fontFamily: theme.bodyFont }}
          >
            {cardCount === 78
              ? t("tarotFanSpread.description1")
              : cardCount === 22
                ? t("tarotFanSpread.description2")
                : t("tarotFanSpread.description3")}
          </p>
        </motion.div>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="w-full max-w-2xl mx-auto mb-8 sm:mb-12 p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-white/80 backdrop-blur-sm shadow-xl border border-[#E5B8AD]/30"
      >
        <h3 className="text-lg sm:text-xl font-serif text-[#4A3F35] mb-3 sm:mb-4 text-center px-2">
          {t("tarotFanSpread.questionTitle")}
          {/* What question would you like the cards to answer? */}
        </h3>

        <div className="relative">
          <textarea
            value={question}
            id="question-input"
            name="question"
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={t("tarotFanSpread.questionPlaceholder")}
            rows={isMobile ? 2 : 3}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border border-[#E8E3DA] focus:outline-none focus:ring-2 focus:ring-[#6D4C92]/20 focus:border-[#6D4C92] transition-colors resize-none text-[#4A3F35] placeholder:text-[#8A7F73]/50 text-sm sm:text-base"
            style={{ backgroundColor: "white" }}
            autoFocus
          />

          <div className="flex justify-end mt-3 sm:mt-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onSubmit}
              className="px-6 sm:px-8 py-2 sm:py-3 rounded-full bg-gradient-to-r from-[#6D4C92] to-[#44304e] text-white font-medium shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
            >
              <span>{t("tarotFanSpread.questionButton")}</span>
              <span className="text-lg sm:text-xl">✨</span>
            </motion.button>
          </div>
        </div>

        <p className="text-[10px] sm:text-xs text-[#8A7F73] mt-2 sm:mt-3 text-center px-2">
          {cardCount === 78
            ? t("tarotFanSpread.description1")
            : cardCount === 22
              ? t("tarotFanSpread.description2")
              : t("tarotFanSpread.description3")}
        </p>
      </motion.div>
    );
  },
);

const TarotFanSpread: React.FC<TarotFanSpreadProps> = ({
  onGetReading,
  category,
  isLoading = false,
  theme,
  useAstriaCardBack = false,
}) => {
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    new Set(),
  );
  const [question, setQuestion] = useState<string>("");
  const [showCards, setShowCards] = useState<boolean>(false);
  const prevCategoryRef = useRef<string>(category);
  const { t } = useTranslation();

  // Sound effects setup
  const [soundsLoaded, setSoundsLoaded] = useState(false);
  const shuffleSound = useRef<Howl | null>(null);
  const cutSound = useRef<Howl | null>(null);
  const cardFlipSound = useRef<Howl | null>(null);
  const magicSound = useRef<Howl | null>(null);

  const currentShuffleId = useRef<number | null>(null);
  const currentCutId = useRef<number | null>(null);
  const currentFlipId = useRef<number | null>(null);
  const currentMagicId = useRef<number | null>(null);
  const isShuffleRunningRef = useRef(false);
  const shuffleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isCutRunningRef = useRef(false);
  const cutEndTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shuffleClickCountRef = useRef(0);
  const cutClickCountRef = useRef(0);
  const isShufflePhase3ReverseRef = useRef(false);
  const isCutPhase3ReverseRef = useRef(false);

  // Initialize sounds
  useEffect(() => {
    if (typeof window === "undefined") return;

    const loadSounds = () => {
      shuffleSound.current = new Howl({
        src: ["/cards/sounds/card-shuffle.mp3"],
        volume: 0.2,
        preload: true,
        html5: true,
        onload: () => checkAllSoundsLoaded(),
        onloaderror: () => checkAllSoundsLoaded(),
      });

      cutSound.current = new Howl({
        src: ["/cards/sounds/cut-sound.mp3", "/cards/sounds/card-shuffle.mp3"],
        volume: 0.4,
        preload: true,
        html5: true,
        onload: () => checkAllSoundsLoaded(),
        onloaderror: () => checkAllSoundsLoaded(),
      });

      cardFlipSound.current = new Howl({
        src: ["/cards/sounds/flip-sound.mp3"],
        volume: 0.1,
        preload: true,
        html5: true,
        onload: () => checkAllSoundsLoaded(),
        onloaderror: () => checkAllSoundsLoaded(),
      });

      magicSound.current = new Howl({
        src: ["/cards/sounds/magic-sparkle.mp3"],
        volume: 0.2,
        preload: true,
        html5: true,
        onload: () => checkAllSoundsLoaded(),
        onloaderror: () => checkAllSoundsLoaded(),
      });
    };

    loadSounds();

    const checkAllSoundsLoaded = () => {
      const shuffleState = shuffleSound.current?.state();
      const cutState = cutSound.current?.state();
      const flipState = cardFlipSound.current?.state();

      if (
        (shuffleState === "loaded" || shuffleState === "unloaded") &&
        (cutState === "loaded" || cutState === "unloaded") &&
        (flipState === "loaded" || flipState === "unloaded")
      ) {
        setSoundsLoaded(true);
      }
    };

    const timeoutId = setTimeout(() => {
      if (!soundsLoaded) {
        setSoundsLoaded(true);
      }
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
      shuffleSound.current?.unload();
      cutSound.current?.unload();
      cardFlipSound.current?.unload();
      magicSound.current?.unload();
    };
  }, []);

  // Memoized play sound function
  const playSound = useCallback(
    (type: "shuffle" | "cut" | "flip" | "magic") => {
      if (!soundsLoaded) return;

      try {
        switch (type) {
          case "shuffle":
            if (currentShuffleId.current !== null) {
              shuffleSound.current?.stop(currentShuffleId.current);
            }
            // Shuffle sound disabled (requested).
            currentShuffleId.current = null;

            // if (shuffleSound.current) {
            //   currentShuffleId.current = shuffleSound.current.play();
            //   if (magicSound.current) {
            //     setTimeout(() => {
            //       currentMagicId.current = magicSound.current?.play() || null;
            //     }, 300);
            //   }
            // }
            break;

          case "cut":
            if (currentCutId.current !== null) {
              cutSound.current?.stop(currentCutId.current);
            }
            if (cutSound.current) {
              currentCutId.current = cutSound.current.play();
            }
            break;

          case "flip":
            if (currentFlipId.current !== null) {
              cardFlipSound.current?.stop(currentFlipId.current);
            }
            if (cardFlipSound.current) {
              currentFlipId.current = cardFlipSound.current.play();
            }
            break;

          case "magic":
            if (currentMagicId.current !== null) {
              magicSound.current?.stop(currentMagicId.current);
            }
            if (magicSound.current) {
              currentMagicId.current = magicSound.current.play();
            }
            break;
        }
      } catch (error) {
        // Silently fail
      }
    },
    [soundsLoaded],
  );

  // Filter cards based on category
  const getCardsByCategory = useCallback((category: string) => {
    const fullDeckCategories = new Set([
      "healjai",
      "challenges",
      "opportunities",
    ]);
    if (fullDeckCategories.has(category)) {
      return tarotCards;
    }
    return tarotCards.filter((card) => card.card_id <= 21);
  }, []);

  const [cards, setCards] = useState<TarotCard[]>(() =>
    fisherYatesShuffle(getCardsByCategory(category)),
  );

  const isShowOriginsCategory =
    category === "challenges" || category === "opportunities";
  const isFullDeckMode = cards.length === 78;
  const isMajorArcanaMode = cards.length === 22;
  const requiredSelections = isFullDeckMode ? 10 : isMajorArcanaMode ? 4 : 1;

  const [animationMode, setAnimationMode] = useState<AnimationMode>("idle");
  const [screenWidth, setScreenWidth] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFloating, setIsFloating] = useState(false);

  // Update cards when category changes
  useEffect(() => {
    if (prevCategoryRef.current !== category) {
      const filteredCards = getCardsByCategory(category);
      setCards(fisherYatesShuffle(filteredCards));
      setSelectedIndices(new Set());
      setShowCards(false);
      setQuestion("");
      prevCategoryRef.current = category;
    }
  }, [category, getCardsByCategory]);

  const [shakingCardIndex, setShakingCardIndex] = useState<number | null>(null);

  const [dailyLimit] = useState<DailyLimitStatus>({
    canGetReading: true,
    remainingReadings: 999,
    nextAvailableTime: null,
    message: "Unlimited testing mode",
  });

  useEffect(() => {
    const updateWidth = () => setScreenWidth(window.innerWidth);
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const isMobile = screenWidth < 640;
  const isTablet = screenWidth >= 640 && screenWidth < 1024;
  const getCardTilt = useCallback(
    (cardId: number) => {
      const normalized = ((cardId * 9301 + 49297) % 233280) / 233280;
      const maxTilt = isMobile ? 5 : 7;
      return (normalized - 0.5) * maxTilt * 2;
    },
    [isMobile],
  );

  const getRadius = useCallback(() => {
    if (cards.length === 78) {
      return isMobile ? 160 : isTablet ? 240 : 320;
    } else if (cards.length === 22) {
      return isMobile ? 130 : isTablet ? 200 : 280;
    } else if (cards.length > 30) {
      return isMobile ? 140 : isTablet ? 200 : 260;
    } else {
      return isMobile ? 110 : isTablet ? 160 : 220;
    }
  }, [cards.length, isMobile, isTablet]);

  const RADIUS = getRadius();
  const CARD_WIDTH = isMobile ? 70 : isTablet ? 110 : 150;
  const CARD_HEIGHT = isMobile ? 110 : isTablet ? 170 : 230;

  // Memoized handlers
  const handleQuestionSubmit = useCallback(() => {
    if (!question.trim()) {
      setQuestion("");
    }
    setShowCards(true);
    playSound("magic");
  }, [question, playSound]);

  const shuffleCards = useCallback(() => {
    if (animationMode !== "idle" || !showCards || isShuffleRunningRef.current)
      return;

    isShuffleRunningRef.current = true;
    if (shuffleTimeoutRef.current) {
      clearTimeout(shuffleTimeoutRef.current);
      shuffleTimeoutRef.current = null;
    }

    playSound("shuffle");
    shuffleClickCountRef.current += 1;
    isShufflePhase3ReverseRef.current = shuffleClickCountRef.current % 3 === 0;

    setSelectedIndices(new Set());
    setAnimationMode("shuffle");
    setIsFloating(true);

    shuffleTimeoutRef.current = setTimeout(() => {
      const shuffled = isShowOriginsCategory
        ? riffleShuffle(cards)
        : fisherYatesShuffle(cards);

      // Use double requestAnimationFrame for smooth transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setCards(shuffled);
          setIsFloating(false);
          setAnimationMode("idle");
          playSound("magic");
          isShuffleRunningRef.current = false;
        });
      });

      shuffleTimeoutRef.current = null;
    }, 4500); // Match animation duration
  }, [animationMode, showCards, cards, playSound, isShowOriginsCategory]);

  useEffect(() => {
    return () => {
      if (shuffleTimeoutRef.current) {
        clearTimeout(shuffleTimeoutRef.current);
      }
      isShuffleRunningRef.current = false;
    };
  }, []);

  const cutCards = useCallback(() => {
    if (animationMode !== "idle" || !showCards || isCutRunningRef.current)
      return;

    isCutRunningRef.current = true;

    // Clear any existing timeouts
    if (cutEndTimeoutRef.current) {
      clearTimeout(cutEndTimeoutRef.current);
      cutEndTimeoutRef.current = null;
    }

    playSound("cut");
    cutClickCountRef.current += 1;
    isCutPhase3ReverseRef.current = cutClickCountRef.current % 3 === 0;

    setSelectedIndices(new Set());
    setAnimationMode("cut"); // Start animation immediately

    // Update card order after animation completes
    cutEndTimeoutRef.current = setTimeout(() => {
      let newOrder: TarotCard[];

      if (isShowOriginsCategory) {
        // For Origins categories, also split into two piles
        const cutIndex = Math.floor(cards.length / 2);

        // You can decide the new order - here I'm moving second half to the front
        // This matches the visual split in Phase 2
        newOrder = [...cards.slice(cutIndex), ...cards.slice(0, cutIndex)];

        // Alternative: if you want a different arrangement, you could use:
        // newOrder = [...cards.slice(0, cutIndex).reverse(), ...cards.slice(cutIndex).reverse()];
      } else {
        // Standard cut - move half the deck to the front
        const cutIndex = Math.floor(cards.length / 2);
        newOrder = [...cards.slice(cutIndex), ...cards.slice(0, cutIndex)];
      }

      // Use double requestAnimationFrame for smooth transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setCards(newOrder);
          setAnimationMode("idle");
          isCutRunningRef.current = false;
        });
      });

      cutEndTimeoutRef.current = null;
    }, 4500); // Match animation duration
  }, [animationMode, showCards, cards, playSound, isShowOriginsCategory]);

  useEffect(() => {
    return () => {
      if (cutEndTimeoutRef.current) {
        clearTimeout(cutEndTimeoutRef.current);
      }
      isCutRunningRef.current = false;
    };
  }, []);

  const handleCardClick = useCallback(
    (index: number) => {
      if (animationMode !== "idle" || !showCards) return;

      setSelectedIndices((prev) => {
        const newSet = new Set(prev);

        if (isFullDeckMode) {
          if (newSet.has(index)) {
            newSet.delete(index);
            playSound("flip");
          } else {
            if (newSet.size < requiredSelections) {
              newSet.add(index);
              playSound("flip");

              if (newSet.size === requiredSelections) {
                setTimeout(() => playSound("magic"), 200);
                toast.success(
                  `${t("toast.tarotFanSpread.cardSelect1")} ${requiredSelections} ${t("toast.tarotFanSpread.cardSelect2")}`,
                  {
                    duration: 2000,
                  },
                );
              }
            } else {
              toast(
                `${t("toast.tarotFanSpread.cardSelect3")} ${requiredSelections} ${t("toast.tarotFanSpread.cardSelect4")}`,
                {
                  icon: "🔮",
                  duration: 2000,
                },
              );
            }
          }
        } else if (isMajorArcanaMode) {
          if (newSet.has(index)) {
            newSet.delete(index);
            playSound("flip");
          } else {
            if (newSet.size < requiredSelections) {
              newSet.add(index);
              playSound("flip");

              if (newSet.size === requiredSelections) {
                setTimeout(() => playSound("magic"), 200);
              }
            } else {
              toast(
                `${t("toast.tarotFanSpread.cardSelect3")} ${requiredSelections} ${t("toast.tarotFanSpread.cardSelect2")}`,
                {
                  icon: "🔮",
                },
              );
            }
          }
        } else {
          if (newSet.has(index)) {
            newSet.delete(index);
            playSound("flip");
          } else {
            newSet.clear();
            newSet.add(index);
            playSound("flip");
            playSound("magic");
          }
        }

        return newSet;
      });
    },
    [
      animationMode,
      showCards,
      isFullDeckMode,
      isMajorArcanaMode,
      requiredSelections,
      playSound,
    ],
  );

  const handleGetReading = useCallback(() => {
    if (selectedIndices.size === requiredSelections) {
      const selectedCards = Array.from(selectedIndices).map(
        (index) => cards[index],
      );
      playSound("magic");

      toast.success(
        `${t("toast.tarotFanSpread.readingText")} ${selectedCards.length} ${t("toast.tarotFanSpread.readingText1")}`,
        {
          duration: 2000,
        },
      );

      onGetReading(selectedCards, question);
    }
  }, [
    selectedIndices,
    requiredSelections,
    cards,
    playSound,
    onGetReading,
    question,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setSelectedIndices(new Set());
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div
        ref={containerRef}
        className="w-full flex flex-col items-center mt-[60px] sm:mt-[80px] md:mt-[110px] mb-16 sm:mb-20 md:mb-24 px-2 sm:px-4 relative"
      >
        {/* Question Input - Show before cards */}
        {!showCards && (
          <QuestionInput
            question={question}
            setQuestion={setQuestion}
            onSubmit={handleQuestionSubmit}
            cardCount={cards.length}
            theme={theme}
          />
        )}

        {/* Cards Section - Only shown after question is submitted */}
        {showCards && (
          <>
            {/* Loading Overlay */}
            {isLoading && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="bg-gradient-to-r from-[#4A1F5E] to-[#2C1039] p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-2xl flex flex-col items-center gap-4 sm:gap-6 max-w-sm mx-4"
                >
                  <div className="relative flex items-center justify-center">
                    <motion.div
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.5, 0.8, 0.5],
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      className="absolute w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-[#B38EDB]/20 blur-xl"
                    />

                    <motion.div
                      animate={{
                        scale: [1, 1.15, 1],
                        rotate: [0, 5, -5, 0],
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      className="relative w-20 h-20 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-[#B38EDB] to-[#7E5AA6] flex items-center justify-center shadow-xl"
                    >
                      <motion.div
                        animate={{
                          rotateY: [0, 180, 360],
                        }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        style={{
                          transformStyle: "preserve-3d",
                        }}
                      >
                        <span className="text-white text-3xl sm:text-5xl">
                          🔮
                        </span>
                      </motion.div>
                    </motion.div>
                  </div>

                  <motion.div className="text-center space-y-2 sm:space-y-3">
                    <motion.p
                      animate={{
                        scale: [1, 1.05, 1],
                        opacity: [0.9, 1, 0.9],
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      className="text-white text-lg sm:text-xl font-medium"
                    >
                      The cards are speaking...
                    </motion.p>

                    <motion.p
                      animate={{
                        opacity: [0.6, 1, 0.6],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1,
                      }}
                      className="text-white/70 text-xs sm:text-sm italic mt-2 sm:mt-4"
                    >
                      Your question guides the energy
                    </motion.p>
                  </motion.div>
                </motion.div>
              </div>
            )}

            {/* Cards Circle Container */}
            <div
              className="w-full flex justify-center items-center relative overflow-visible"
              style={{ height: RADIUS * 2 }}
            >
              {cards.map((card, index) => {
                // Calculate angles starting from top (12 o'clock position)
                const angleStep = 360 / cards.length;
                const angleDeg = index * angleStep - 90; // Subtract 90 to start from top
                const angleRad = (angleDeg * Math.PI) / 180;

                const x = RADIUS * Math.cos(angleRad);
                const y = RADIUS * Math.sin(angleRad);

                const isSelected = selectedIndices.has(index);
                const isShaking = shakingCardIndex === index;
                const selectionOrder = isSelected
                  ? Array.from(selectedIndices).indexOf(index) + 1
                  : null;

                let animateX = x;
                let animateY = y;
                let animateRotate = angleDeg + 90; // This keeps cards facing outward
                let animateScale = isSelected ? 1.01 : 1;

                if (animationMode === "shuffle") {
                  if (isShowOriginsCategory) {
                    // For Origins categories, we'll use the same animation
                    // Positions will be handled by keyframes
                    animateX = x;
                    animateY = y;
                    animateRotate = angleDeg + 90;
                    animateScale = 1;
                  } else {
                    animateX = x;
                    animateY = y;
                    animateRotate = angleDeg + 90;
                    animateScale = 1;
                  }
                }

                if (animationMode === "cut") {
                  if (isShowOriginsCategory) {
                    // For cut animation, we use the cut keyframes
                    animateX = x;
                    animateY = y;
                    animateRotate = angleDeg + 90;
                    animateScale = 1;
                  } else {
                    // For cumulative gathering animation
                    // Positions will be handled by keyframes
                    animateX = x;
                    animateY = y;
                    animateRotate = angleDeg + 90;
                    animateScale = 1;
                  }
                }

                const selectedOutwardFactor = isSelected
                  ? isMobile
                    ? 1.05
                    : isTablet
                      ? 1.06
                      : 1.08
                  : 1;
                const baseX = animateX * selectedOutwardFactor;
                const baseY = animateY * selectedOutwardFactor;
                const baseRotate = animateRotate;

                // Responsive selection order badge size
                const badgeSize = isMobile ? 5 : 6;
                const isShuffle = animationMode === "shuffle";
                const isShowOriginsCut =
                  isShowOriginsCategory && animationMode === "cut";
                const tilt = getCardTilt(card.card_id);

                const cardAnimate = isShaking
                  ? {
                      x: [
                        baseX,
                        baseX - (isMobile ? 6 : 10),
                        baseX + (isMobile ? 6 : 10),
                        baseX - (isMobile ? 6 : 10),
                        baseX + (isMobile ? 6 : 10),
                        baseX - (isMobile ? 3 : 5),
                        baseX + (isMobile ? 3 : 5),
                        baseX,
                      ],
                      y: [
                        baseY,
                        baseY - (isMobile ? 3 : 5),
                        baseY + (isMobile ? 3 : 5),
                        baseY - (isMobile ? 3 : 5),
                        baseY + (isMobile ? 3 : 5),
                        baseY - (isMobile ? 2 : 3),
                        baseY + (isMobile ? 2 : 3),
                        baseY,
                      ],
                      rotate: [
                        baseRotate,
                        baseRotate - (isMobile ? 3 : 5),
                        baseRotate + (isMobile ? 3 : 5),
                        baseRotate - (isMobile ? 3 : 5),
                        baseRotate + (isMobile ? 3 : 5),
                        baseRotate - (isMobile ? 2 : 3),
                        baseRotate + (isMobile ? 2 : 3),
                        baseRotate,
                      ],
                      opacity:
                        selectedIndices.size === 0 ? 1 : isSelected ? 1 : 0.9,
                      scale: animateScale,
                      rotateX: isFloating ? (isMobile ? 10 : 15) : 0,
                      rotateY: isFloating ? (isMobile ? -6 : -10) : 0,
                      z: isFloating ? (isMobile ? 20 : 40) : 0,
                    }
                  : animationMode === "shuffle"
                    ? (() => {
                        // SHUFFLE ANIMATION - Same Phase 1 and 3 as cut, different Phase 2
                        const totalCards = cards.length;

                        // Pre-calculate all target positions (starting from top)
                        const gatherPositions: {
                          x: number;
                          y: number;
                          angle: number;
                        }[] = [];
                        for (let i = 0; i < totalCards; i++) {
                          const angleRad =
                            (i * 2 * Math.PI) / totalCards - Math.PI / 2;
                          gatherPositions.push({
                            x: RADIUS * Math.cos(angleRad),
                            y: RADIUS * Math.sin(angleRad),
                            angle: (angleRad * 180) / Math.PI + 90,
                          });
                        }
                        const phase3Order = getPhase3TraversalOrder(
                          totalCards,
                          isShufflePhase3ReverseRef.current,
                        );
                        const phase3OrderByCardIndex = new Array<number>(
                          totalCards,
                        );
                        for (let i = 0; i < phase3Order.length; i++) {
                          phase3OrderByCardIndex[phase3Order[i]] = i;
                        }
                        const thisCardPhase3Order =
                          phase3OrderByCardIndex[index];

                        const numKeyframes = 90;
                        const xKeyframes: number[] = [];
                        const yKeyframes: number[] = [];
                        const rotateKeyframes: number[] = [];
                        const scaleKeyframes: number[] = [];
                        const zKeyframes: number[] = [];

                        for (let frame = 0; frame <= numKeyframes; frame++) {
                          const progress = frame / numKeyframes;

                          if (progress < 0.4) {
                            // PHASE 1: Forward Cumulative Gathering (0-40%) - SAME AS CUT
                            // Cards gather from last to first, building the deck progressively

                            const phaseProgress = progress / 0.4;

                            // Calculate how many cards have been gathered from the end
                            const gatheredCount = Math.floor(
                              phaseProgress * totalCards,
                            );

                            // The gathering point is the position of the first card that hasn't been gathered yet
                            const gatherPointIndex = Math.max(
                              0,
                              totalCards - 1 - gatheredCount,
                            );
                            const gatherPoint =
                              gatherPositions[gatherPointIndex];

                            // Determine if this card has been gathered
                            const isGathered =
                              index >= totalCards - gatheredCount;

                            if (isGathered) {
                              // This card is part of the gathered deck
                              const positionInDeck =
                                index - (totalCards - gatheredCount);

                              // TIGHTER STACKING - Reduced from 2-3 to 0.8-1.2
                              const stackOffset =
                                positionInDeck * (isMobile ? 0.8 : 1.2);

                              // Target position: at gather point, stacked vertically
                              const targetX = gatherPoint.x;
                              const targetY = gatherPoint.y - stackOffset;

                              // Cards move to their deck position
                              const joinProgress = Math.min(
                                1,
                                phaseProgress * 2,
                              );

                              const currentX = x + (targetX - x) * joinProgress;
                              const currentY = y + (targetY - y) * joinProgress;

                              xKeyframes.push(currentX);
                              yKeyframes.push(currentY);

                              // Rotate to align with gather point
                              const targetRotate = gatherPoint.angle;
                              const currentRotate =
                                angleDeg +
                                90 +
                                (targetRotate - (angleDeg + 90)) * joinProgress;
                              rotateKeyframes.push(currentRotate);

                              scaleKeyframes.push(0.95);
                              // TIGHTER Z-INDEX - Reduced from 3 to 1
                              zKeyframes.push(20 + positionInDeck * 1);
                            } else {
                              // This card hasn't been gathered yet - stay at original position
                              xKeyframes.push(x);
                              yKeyframes.push(y);
                              rotateKeyframes.push(angleDeg + 90);
                              scaleKeyframes.push(1);
                              zKeyframes.push(0);
                            }
                          } else if (progress < 0.6) {
                            // PHASE 2: Shuffle Mixing (40-60%) - DIFFERENT FROM CUT
                            // Cards mix and exchange positions randomly

                            const phaseProgress = (progress - 0.4) / 0.2;

                            // All cards are now gathered at the first card's position
                            const gatherPoint = gatherPositions[0];

                            // Create random target positions for shuffle effect
                            // Cards will swap places in a chaotic but controlled way

                            // Generate random offset for each card based on its index
                            // This creates a unique but deterministic shuffle pattern
                            const randomSeed = ((index * 7919) % 10000) / 10000; // Pseudo-random based on index

                            // Shuffle parameters
                            const swirlRadius = isMobile ? 80 : 120;
                            const swirlAngle1 = randomSeed * Math.PI * 2;
                            const swirlAngle2 =
                              (randomSeed + 0.5) * Math.PI * 2;

                            // Cards move in a swirling pattern around the center
                            // First half of phase: cards spread out in a chaotic pattern
                            // Second half: cards start coming back together

                            let targetX, targetY, targetRotate;

                            if (phaseProgress < 0.5) {
                              // First half of shuffle - cards spread out
                              const spreadProgress = phaseProgress * 2; // 0 to 1

                              // Use sine waves to create organic movement
                              const angle =
                                swirlAngle1 + spreadProgress * Math.PI * 4;
                              const radius =
                                swirlRadius *
                                Math.sin(spreadProgress * Math.PI);

                              targetX =
                                gatherPoint.x + Math.cos(angle) * radius;
                              targetY =
                                gatherPoint.y -
                                30 +
                                Math.sin(angle) * radius * 0.5;

                              // Random rotation during spread
                              targetRotate =
                                gatherPoint.angle +
                                (randomSeed - 0.5) * 90 * spreadProgress;
                            } else {
                              // Second half of shuffle - cards start regrouping
                              const regroupProgress = (phaseProgress - 0.5) * 2; // 0 to 1

                              // Cards begin to form new pairs/groups
                              const pairIndex = Math.floor(index / 2);
                              const isFirstInPair = index % 2 === 0;

                              // Cards move towards their new positions
                              if (isFirstInPair) {
                                // First card in pair moves left/up
                                targetX =
                                  gatherPoint.x - 40 * (1 - regroupProgress);
                                targetY =
                                  gatherPoint.y - 60 + 20 * regroupProgress;
                              } else {
                                // Second card in pair moves right/down
                                targetX =
                                  gatherPoint.x + 40 * (1 - regroupProgress);
                                targetY =
                                  gatherPoint.y - 60 - 20 * regroupProgress;
                              }

                              targetRotate =
                                gatherPoint.angle +
                                (isFirstInPair ? -10 : 10) *
                                  (1 - regroupProgress);
                            }

                            const prevX = xKeyframes[xKeyframes.length - 1];
                            const prevY = yKeyframes[yKeyframes.length - 1];
                            const prevRotate =
                              rotateKeyframes[rotateKeyframes.length - 1];

                            xKeyframes.push(
                              prevX + (targetX - prevX) * phaseProgress,
                            );
                            yKeyframes.push(
                              prevY + (targetY - prevY) * phaseProgress,
                            );
                            rotateKeyframes.push(
                              prevRotate +
                                (targetRotate - prevRotate) * phaseProgress,
                            );

                            // Scale and Z-index vary during shuffle
                            scaleKeyframes.push(
                              0.92 +
                                Math.sin(phaseProgress * Math.PI * 4) * 0.05,
                            );
                            zKeyframes.push(
                              40 + Math.sin(phaseProgress * Math.PI * 2) * 20,
                            );
                          } else {
                            // PHASE 3: Reverse Cumulative Placement (60-100%) - SAME AS CUT
                            // The deck travels to each position and places cards one by one

                            const phaseProgress = (progress - 0.6) / 0.4;

                            // Calculate how many cards have been placed so far
                            const placedCount = Math.floor(
                              phaseProgress * totalCards,
                            );

                            // The deck's current position is at the next card
                            const deckOrderIndex = Math.min(
                              placedCount,
                              totalCards - 1,
                            );
                            const deckPosition =
                              gatherPositions[phase3Order[deckOrderIndex]];

                            // Determine if this card has been placed or is still in the deck
                            const isPlaced = thisCardPhase3Order < placedCount;

                            if (isPlaced) {
                              // This card has been placed - it should be at its original position
                              const targetPos = gatherPositions[index];

                              xKeyframes.push(targetPos.x);
                              yKeyframes.push(targetPos.y);
                              rotateKeyframes.push(targetPos.angle);
                              scaleKeyframes.push(1);
                              zKeyframes.push(0);
                            } else {
                              // This card is still in the deck
                              const remainingCount = totalCards - placedCount;
                              const positionInDeck =
                                thisCardPhase3Order - placedCount;

                              // TIGHTER STACKING - Reduced from 2-3 to 0.8-1.2
                              const stackOffset =
                                positionInDeck * (isMobile ? 0.8 : 1.2);

                              // Calculate current deck position as it travels
                              let currentDeckX, currentDeckY;

                              if (phaseProgress < 1 / totalCards) {
                                currentDeckX =
                                  gatherPositions[phase3Order[0]].x;
                                currentDeckY =
                                  gatherPositions[phase3Order[0]].y;
                              } else {
                                const currentDeckIndex = Math.min(
                                  Math.floor(phaseProgress * totalCards),
                                  totalCards - 2,
                                );
                                const nextDeckIndex = currentDeckIndex + 1;
                                const subProgress =
                                  phaseProgress * totalCards - currentDeckIndex;

                                const currentPos =
                                  gatherPositions[
                                    phase3Order[currentDeckIndex]
                                  ];
                                const nextPos =
                                  gatherPositions[phase3Order[nextDeckIndex]];

                                currentDeckX =
                                  currentPos.x +
                                  (nextPos.x - currentPos.x) * subProgress;
                                currentDeckY =
                                  currentPos.y +
                                  (nextPos.y - currentPos.y) * subProgress;
                              }

                              const currentX = currentDeckX;
                              const currentY = currentDeckY - stackOffset;

                              xKeyframes.push(currentX);
                              yKeyframes.push(currentY);
                              rotateKeyframes.push(deckPosition.angle);
                              scaleKeyframes.push(0.95);
                              // TIGHTER Z-INDEX - Reduced from 3 to 1
                              zKeyframes.push(30 + positionInDeck * 1);
                            }
                          }
                        }

                        return {
                          opacity: 1,
                          x: xKeyframes,
                          y: yKeyframes,
                          rotate: rotateKeyframes,
                          scale: scaleKeyframes,
                          rotateX: 0,
                          rotateY: 0,
                          z: zKeyframes,
                        };
                      })()
                    : animationMode === "cut"
                      ? (() => {
                          if (isShowOriginsCategory) {
                            // Same cumulative gathering animation for Origins categories with 2-pile split
                            const totalCards = cards.length;
                            const cutIndex = Math.floor(totalCards / 2);
                            const goesToFirstPile = index < cutIndex;

                            const numKeyframes = 90;
                            const xKeyframes: number[] = [];
                            const yKeyframes: number[] = [];
                            const rotateKeyframes: number[] = [];
                            const scaleKeyframes: number[] = [];
                            const zKeyframes: number[] = [];

                            // Pre-calculate all target positions for each phase (starting from top)
                            const gatherPositions: {
                              x: number;
                              y: number;
                              angle: number;
                            }[] = [];
                            for (let i = 0; i < totalCards; i++) {
                              const angleRad =
                                (i * 2 * Math.PI) / totalCards - Math.PI / 2; // Subtract 90 degrees to start from top
                              gatherPositions.push({
                                x: RADIUS * Math.cos(angleRad),
                                y: RADIUS * Math.sin(angleRad),
                                angle: (angleRad * 180) / Math.PI + 90,
                              });
                            }
                            const phase3Order = getPhase3TraversalOrder(
                              totalCards,
                              isCutPhase3ReverseRef.current,
                            );
                            const phase3OrderByCardIndex = new Array<number>(
                              totalCards,
                            );
                            for (let i = 0; i < phase3Order.length; i++) {
                              phase3OrderByCardIndex[phase3Order[i]] = i;
                            }
                            const thisCardPhase3Order =
                              phase3OrderByCardIndex[index];

                            for (
                              let frame = 0;
                              frame <= numKeyframes;
                              frame++
                            ) {
                              const progress = frame / numKeyframes;

                              if (progress < 0.4) {
                                // PHASE 1: Forward Cumulative Gathering (0-40%)
                                const phaseProgress = progress / 0.4;
                                const gatheredCount = Math.floor(
                                  phaseProgress * totalCards,
                                );
                                const gatherPointIndex = Math.max(
                                  0,
                                  totalCards - 1 - gatheredCount,
                                );
                                const gatherPoint =
                                  gatherPositions[gatherPointIndex];
                                const isGathered =
                                  index >= totalCards - gatheredCount;

                                if (isGathered) {
                                  const positionInDeck =
                                    index - (totalCards - gatheredCount);

                                  // TIGHTER STACKING
                                  const stackOffset =
                                    positionInDeck * (isMobile ? 0.8 : 1.2);
                                  const targetX = gatherPoint.x;
                                  const targetY = gatherPoint.y - stackOffset;
                                  const joinProgress = Math.min(
                                    1,
                                    phaseProgress * 2,
                                  );

                                  const currentX =
                                    x + (targetX - x) * joinProgress;
                                  const currentY =
                                    y + (targetY - y) * joinProgress;

                                  xKeyframes.push(currentX);
                                  yKeyframes.push(currentY);

                                  const targetRotate = gatherPoint.angle;
                                  const currentRotate =
                                    angleDeg +
                                    90 +
                                    (targetRotate - (angleDeg + 90)) *
                                      joinProgress;
                                  rotateKeyframes.push(currentRotate);

                                  scaleKeyframes.push(0.95);
                                  // TIGHTER Z-INDEX
                                  zKeyframes.push(20 + positionInDeck * 1);
                                } else {
                                  xKeyframes.push(x);
                                  yKeyframes.push(y);
                                  rotateKeyframes.push(angleDeg + 90);
                                  scaleKeyframes.push(1);
                                  zKeyframes.push(0);
                                }
                              } else if (progress < 0.6) {
                                // PHASE 2: Deck Split into 2 piles (40-60%)
                                const phaseProgress = (progress - 0.4) / 0.2;
                                const gatherPoint = gatherPositions[0];
                                const splitDistance = isMobile ? 120 : 180;

                                const firstPilePos = {
                                  x: gatherPoint.x - splitDistance * 0.7,
                                  y: gatherPoint.y - 20,
                                };

                                const secondPilePos = {
                                  x: gatherPoint.x + splitDistance * 0.7,
                                  y: gatherPoint.y - 20,
                                };

                                if (goesToFirstPile) {
                                  const pileIndex = index;
                                  const reversedPileIndex =
                                    cutIndex - 1 - pileIndex;
                                  // TIGHTER PILE STACKING
                                  const stackOffset =
                                    reversedPileIndex * (isMobile ? 1.5 : 2);
                                  const targetX = firstPilePos.x;
                                  const targetY = firstPilePos.y - stackOffset;

                                  const prevX =
                                    xKeyframes[xKeyframes.length - 1];
                                  const prevY =
                                    yKeyframes[yKeyframes.length - 1];
                                  const prevRotate =
                                    rotateKeyframes[rotateKeyframes.length - 1];

                                  xKeyframes.push(
                                    prevX + (targetX - prevX) * phaseProgress,
                                  );
                                  yKeyframes.push(
                                    prevY + (targetY - prevY) * phaseProgress,
                                  );

                                  const targetRotate = gatherPoint.angle - 5;
                                  rotateKeyframes.push(
                                    prevRotate +
                                      (targetRotate - prevRotate) *
                                        phaseProgress,
                                  );

                                  scaleKeyframes.push(0.94);
                                  // TIGHTER Z-INDEX
                                  zKeyframes.push(40 + reversedPileIndex * 1);
                                } else {
                                  const pileIndex = index - cutIndex;
                                  const maxPileIndex =
                                    totalCards - cutIndex - 1;
                                  const reversedPileIndex =
                                    maxPileIndex - pileIndex;
                                  // TIGHTER PILE STACKING
                                  const stackOffset =
                                    reversedPileIndex * (isMobile ? 1.5 : 2);
                                  const targetX = secondPilePos.x;
                                  const targetY = secondPilePos.y - stackOffset;

                                  const prevX =
                                    xKeyframes[xKeyframes.length - 1];
                                  const prevY =
                                    yKeyframes[yKeyframes.length - 1];
                                  const prevRotate =
                                    rotateKeyframes[rotateKeyframes.length - 1];

                                  xKeyframes.push(
                                    prevX + (targetX - prevX) * phaseProgress,
                                  );
                                  yKeyframes.push(
                                    prevY + (targetY - prevY) * phaseProgress,
                                  );

                                  const targetRotate = gatherPoint.angle + 5;
                                  rotateKeyframes.push(
                                    prevRotate +
                                      (targetRotate - prevRotate) *
                                        phaseProgress,
                                  );

                                  scaleKeyframes.push(0.94);
                                  // TIGHTER Z-INDEX
                                  zKeyframes.push(40 + reversedPileIndex * 1);
                                }
                              } else {
                                // PHASE 3: Reverse Cumulative Placement (60-100%)
                                const phaseProgress = (progress - 0.6) / 0.4;
                                const placedCount = Math.floor(
                                  phaseProgress * totalCards,
                                );
                                const deckPosition =
                                  gatherPositions[
                                    phase3Order[
                                      Math.min(placedCount, totalCards - 1)
                                    ]
                                  ];
                                const isPlaced =
                                  thisCardPhase3Order < placedCount;

                                if (isPlaced) {
                                  const targetPos = gatherPositions[index];
                                  xKeyframes.push(targetPos.x);
                                  yKeyframes.push(targetPos.y);
                                  rotateKeyframes.push(targetPos.angle);
                                  scaleKeyframes.push(1);
                                  zKeyframes.push(0);
                                } else {
                                  const remainingCount =
                                    totalCards - placedCount;
                                  const positionInDeck =
                                    thisCardPhase3Order - placedCount;

                                  // TIGHTER STACKING
                                  const stackOffset =
                                    positionInDeck * (isMobile ? 0.8 : 1.2);

                                  let currentDeckX, currentDeckY;

                                  if (phaseProgress < 1 / totalCards) {
                                    currentDeckX =
                                      gatherPositions[phase3Order[0]].x;
                                    currentDeckY =
                                      gatherPositions[phase3Order[0]].y;
                                  } else {
                                    const currentDeckIndex = Math.min(
                                      Math.floor(phaseProgress * totalCards),
                                      totalCards - 2,
                                    );
                                    const nextDeckIndex = currentDeckIndex + 1;
                                    const subProgress =
                                      phaseProgress * totalCards -
                                      currentDeckIndex;

                                    const currentPos =
                                      gatherPositions[
                                        phase3Order[currentDeckIndex]
                                      ];
                                    const nextPos =
                                      gatherPositions[
                                        phase3Order[nextDeckIndex]
                                      ];

                                    currentDeckX =
                                      currentPos.x +
                                      (nextPos.x - currentPos.x) * subProgress;
                                    currentDeckY =
                                      currentPos.y +
                                      (nextPos.y - currentPos.y) * subProgress;
                                  }

                                  const currentX = currentDeckX;
                                  const currentY = currentDeckY - stackOffset;

                                  xKeyframes.push(currentX);
                                  yKeyframes.push(currentY);
                                  rotateKeyframes.push(deckPosition.angle);
                                  scaleKeyframes.push(0.95);
                                  // TIGHTER Z-INDEX
                                  zKeyframes.push(30 + positionInDeck * 1);
                                }
                              }
                            }

                            return {
                              opacity: 1,
                              x: xKeyframes,
                              y: yKeyframes,
                              rotate: rotateKeyframes,
                              scale: scaleKeyframes,
                              rotateX: 0,
                              rotateY: 0,
                              z: zKeyframes,
                            };
                          } else {
                            // Regular decks cut animation - same as before with 2-pile split
                            const totalCards = cards.length;
                            const cutIndex = Math.floor(totalCards / 2);
                            const goesToFirstPile = index < cutIndex;

                            const numKeyframes = 90;
                            const xKeyframes: number[] = [];
                            const yKeyframes: number[] = [];
                            const rotateKeyframes: number[] = [];
                            const scaleKeyframes: number[] = [];
                            const zKeyframes: number[] = [];

                            // Pre-calculate all target positions
                            const gatherPositions: {
                              x: number;
                              y: number;
                              angle: number;
                            }[] = [];
                            for (let i = 0; i < totalCards; i++) {
                              const angleRad =
                                (i * 2 * Math.PI) / totalCards - Math.PI / 2;
                              gatherPositions.push({
                                x: RADIUS * Math.cos(angleRad),
                                y: RADIUS * Math.sin(angleRad),
                                angle: (angleRad * 180) / Math.PI + 90,
                              });
                            }
                            const phase3Order = getPhase3TraversalOrder(
                              totalCards,
                              isCutPhase3ReverseRef.current,
                            );
                            const phase3OrderByCardIndex = new Array<number>(
                              totalCards,
                            );
                            for (let i = 0; i < phase3Order.length; i++) {
                              phase3OrderByCardIndex[phase3Order[i]] = i;
                            }
                            const thisCardPhase3Order =
                              phase3OrderByCardIndex[index];

                            for (
                              let frame = 0;
                              frame <= numKeyframes;
                              frame++
                            ) {
                              const progress = frame / numKeyframes;

                              if (progress < 0.4) {
                                // PHASE 1: Forward Cumulative Gathering (0-40%)
                                const phaseProgress = progress / 0.4;
                                const gatheredCount = Math.floor(
                                  phaseProgress * totalCards,
                                );
                                const gatherPointIndex = Math.max(
                                  0,
                                  totalCards - 1 - gatheredCount,
                                );
                                const gatherPoint =
                                  gatherPositions[gatherPointIndex];
                                const isGathered =
                                  index >= totalCards - gatheredCount;

                                if (isGathered) {
                                  const positionInDeck =
                                    index - (totalCards - gatheredCount);

                                  // TIGHTER STACKING
                                  const stackOffset =
                                    positionInDeck * (isMobile ? 0.8 : 1.2);
                                  const targetX = gatherPoint.x;
                                  const targetY = gatherPoint.y - stackOffset;
                                  const joinProgress = Math.min(
                                    1,
                                    phaseProgress * 2,
                                  );

                                  xKeyframes.push(
                                    x + (targetX - x) * joinProgress,
                                  );
                                  yKeyframes.push(
                                    y + (targetY - y) * joinProgress,
                                  );

                                  const targetRotate = gatherPoint.angle;
                                  rotateKeyframes.push(
                                    angleDeg +
                                      90 +
                                      (targetRotate - (angleDeg + 90)) *
                                        joinProgress,
                                  );

                                  scaleKeyframes.push(0.95);
                                  // TIGHTER Z-INDEX
                                  zKeyframes.push(20 + positionInDeck * 1);
                                } else {
                                  xKeyframes.push(x);
                                  yKeyframes.push(y);
                                  rotateKeyframes.push(angleDeg + 90);
                                  scaleKeyframes.push(1);
                                  zKeyframes.push(0);
                                }
                              } else if (progress < 0.6) {
                                // PHASE 2: Deck Split into 2 piles (40-60%)
                                const phaseProgress = (progress - 0.4) / 0.2;
                                const gatherPoint = gatherPositions[0];

                                const splitDistance = isMobile ? 120 : 180;

                                const firstPilePos = {
                                  x: gatherPoint.x - splitDistance * 0.7,
                                  y: gatherPoint.y - 20,
                                };

                                const secondPilePos = {
                                  x: gatherPoint.x + splitDistance * 0.7,
                                  y: gatherPoint.y - 20,
                                };

                                if (goesToFirstPile) {
                                  const pileIndex = index;
                                  const reversedPileIndex =
                                    cutIndex - 1 - pileIndex;
                                  // TIGHTER PILE STACKING
                                  const stackOffset =
                                    reversedPileIndex * (isMobile ? 1.5 : 2);
                                  const targetX = firstPilePos.x;
                                  const targetY = firstPilePos.y - stackOffset;

                                  const prevX =
                                    xKeyframes[xKeyframes.length - 1];
                                  const prevY =
                                    yKeyframes[yKeyframes.length - 1];
                                  const prevRotate =
                                    rotateKeyframes[rotateKeyframes.length - 1];

                                  xKeyframes.push(
                                    prevX + (targetX - prevX) * phaseProgress,
                                  );
                                  yKeyframes.push(
                                    prevY + (targetY - prevY) * phaseProgress,
                                  );

                                  const targetRotate = gatherPoint.angle - 5;
                                  rotateKeyframes.push(
                                    prevRotate +
                                      (targetRotate - prevRotate) *
                                        phaseProgress,
                                  );

                                  scaleKeyframes.push(0.94);
                                  // TIGHTER Z-INDEX
                                  zKeyframes.push(40 + reversedPileIndex * 1);
                                } else {
                                  const pileIndex = index - cutIndex;
                                  const maxPileIndex =
                                    totalCards - cutIndex - 1;
                                  const reversedPileIndex =
                                    maxPileIndex - pileIndex;
                                  // TIGHTER PILE STACKING
                                  const stackOffset =
                                    reversedPileIndex * (isMobile ? 1.5 : 2);
                                  const targetX = secondPilePos.x;
                                  const targetY = secondPilePos.y - stackOffset;

                                  const prevX =
                                    xKeyframes[xKeyframes.length - 1];
                                  const prevY =
                                    yKeyframes[yKeyframes.length - 1];
                                  const prevRotate =
                                    rotateKeyframes[rotateKeyframes.length - 1];

                                  xKeyframes.push(
                                    prevX + (targetX - prevX) * phaseProgress,
                                  );
                                  yKeyframes.push(
                                    prevY + (targetY - prevY) * phaseProgress,
                                  );

                                  const targetRotate = gatherPoint.angle + 5;
                                  rotateKeyframes.push(
                                    prevRotate +
                                      (targetRotate - prevRotate) *
                                        phaseProgress,
                                  );

                                  scaleKeyframes.push(0.94);
                                  // TIGHTER Z-INDEX
                                  zKeyframes.push(40 + reversedPileIndex * 1);
                                }
                              } else {
                                // PHASE 3: Reverse Cumulative Placement (60-100%)
                                const phaseProgress = (progress - 0.6) / 0.4;
                                const placedCount = Math.floor(
                                  phaseProgress * totalCards,
                                );
                                const deckPosition =
                                  gatherPositions[
                                    phase3Order[
                                      Math.min(placedCount, totalCards - 1)
                                    ]
                                  ];
                                const isPlaced =
                                  thisCardPhase3Order < placedCount;

                                if (isPlaced) {
                                  const targetPos = gatherPositions[index];
                                  xKeyframes.push(targetPos.x);
                                  yKeyframes.push(targetPos.y);
                                  rotateKeyframes.push(targetPos.angle);
                                  scaleKeyframes.push(1);
                                  zKeyframes.push(0);
                                } else {
                                  const remainingCount =
                                    totalCards - placedCount;
                                  const positionInDeck =
                                    thisCardPhase3Order - placedCount;

                                  // TIGHTER STACKING
                                  const stackOffset =
                                    positionInDeck * (isMobile ? 0.8 : 1.2);

                                  let currentDeckX, currentDeckY;

                                  if (phaseProgress < 1 / totalCards) {
                                    currentDeckX =
                                      gatherPositions[phase3Order[0]].x;
                                    currentDeckY =
                                      gatherPositions[phase3Order[0]].y;
                                  } else {
                                    const currentDeckIndex = Math.min(
                                      Math.floor(phaseProgress * totalCards),
                                      totalCards - 2,
                                    );
                                    const nextDeckIndex = currentDeckIndex + 1;
                                    const subProgress =
                                      phaseProgress * totalCards -
                                      currentDeckIndex;

                                    const currentPos =
                                      gatherPositions[
                                        phase3Order[currentDeckIndex]
                                      ];
                                    const nextPos =
                                      gatherPositions[
                                        phase3Order[nextDeckIndex]
                                      ];

                                    currentDeckX =
                                      currentPos.x +
                                      (nextPos.x - currentPos.x) * subProgress;
                                    currentDeckY =
                                      currentPos.y +
                                      (nextPos.y - currentPos.y) * subProgress;
                                  }

                                  xKeyframes.push(currentDeckX);
                                  yKeyframes.push(currentDeckY - stackOffset);
                                  rotateKeyframes.push(deckPosition.angle);
                                  scaleKeyframes.push(0.95);
                                  // TIGHTER Z-INDEX
                                  zKeyframes.push(30 + positionInDeck * 1);
                                }
                              }
                            }

                            return {
                              opacity: 1,
                              x: xKeyframes,
                              y: yKeyframes,
                              rotate: rotateKeyframes,
                              scale: scaleKeyframes,
                              rotateX: 0,
                              rotateY: 0,
                              z: zKeyframes,
                            };
                          }
                        })()
                      : {
                          opacity:
                            selectedIndices.size === 0
                              ? 1
                              : isSelected
                                ? 1
                                : 0.9,
                          x: baseX,
                          y: baseY,
                          rotate: baseRotate,
                          scale: animateScale,
                          rotateX: isFloating ? (isMobile ? 10 : 15) : 0,
                          rotateY: isFloating ? (isMobile ? -6 : -10) : 0,
                          z: isFloating ? (isMobile ? 20 : 40) : 0,
                        };

                const cardTransition = isShaking
                  ? {
                      duration: 0.5,
                      ease: "easeInOut" as const,
                      times: [0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 1],
                    }
                  : animationMode === "shuffle"
                    ? {
                        duration: 4.5,
                        ease: "linear" as const,
                        times: Array.from({ length: 91 }, (_, i) => i / 90),
                        delay: 0,
                      }
                    : animationMode === "cut"
                      ? {
                          duration: 4.5,
                          ease: "linear" as const,
                          times: Array.from({ length: 91 }, (_, i) => i / 90),
                          delay: 0,
                        }
                      : {
                          duration: 0.8,
                          ease: "easeInOut" as const,
                        };

                return (
                  <motion.div
                    key={card.card_id}
                    className="absolute cursor-pointer"
                    style={{
                      transformOrigin: "center center",
                      zIndex: isSelected ? index + 2 : index,
                      perspective: 1000,
                    }}
                    animate={cardAnimate}
                    transition={cardTransition}
                    whileHover={
                      animationMode === "idle" && !isLoading && showCards
                        ? {
                            scale: 1.02,
                            transition: { duration: 0.4 },
                          }
                        : {}
                    }
                    onClick={() => !isLoading && handleCardClick(index)}
                  >
                    <motion.div
                      animate={{
                        rotateY: isSelected && !isShaking ? 180 : 0,
                        scale: isShaking ? [1, 1.1, 1] : 1,
                      }}
                      transition={{
                        duration: isShaking ? 0.3 : 0.6,
                        times: isShaking ? [0, 0.3, 0.6] : undefined,
                        ease: "easeInOut",
                      }}
                      style={{
                        width: CARD_WIDTH,
                        height: CARD_HEIGHT,
                        position: "relative",
                        transformStyle: "preserve-3d",
                      }}
                    >
                      {/* Card Back */}
                      <div
                        className={`absolute w-full h-full rounded-xl sm:rounded-2xl overflow-hidden
  shadow-[0_4px_10px_rgba(0,0,0,0.15)] 
  border border-[#B38EDB]/30 
  flex items-center justify-center ${!useAstriaCardBack ? "bg-gradient-to-br from-[#4A1F5E] via-[#3B1A4A] to-[#1E0C25]" : ""}`}
                        style={{ backfaceVisibility: "hidden" }}
                      >
                        {useAstriaCardBack && (
                          <div className="absolute inset-0">
                            <AstriaCardBack />
                          </div>
                        )}
                        <div className="relative flex items-center justify-center">
                          {!useAstriaCardBack && (
                          <div
                            style={{
                              width: CARD_WIDTH * 0.5,
                              height: CARD_WIDTH * 0.5,
                              border: "1px solid rgba(255,255,255,0.4)",
                              transform: "rotate(45deg)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <div
                              style={{
                                width: CARD_WIDTH * 0.2,
                                height: CARD_WIDTH * 0.2,
                                border: "1px solid rgba(255,255,255,0.5)",
                                transform: "rotate(45deg)",
                              }}
                            />
                          </div>
                          )}
                          {(isFullDeckMode || isMajorArcanaMode) &&
                            selectionOrder && (
                              <div
                                className={`absolute top-1 sm:top-2 right-1 sm:right-2 w-${badgeSize} h-${badgeSize} rounded-full bg-[#6D4C92] text-white flex items-center justify-center text-[10px] sm:text-sm font-bold`}
                              >
                                {selectionOrder}
                              </div>
                            )}
                        </div>
                      </div>

                      {/* Card Front */}
                      <div
                        className="absolute w-full h-full rounded-xl sm:rounded-2xl shadow-lg overflow-hidden"
                        style={{
                          transform: "rotateY(180deg)",
                          backfaceVisibility: "hidden",
                        }}
                      >
                        <img
                          src={card.image_url}
                          alt={card.card_name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>

            {/* Buttons Container */}
            {isMobile ? (
              <div className="w-full flex flex-col items-center mt-[70px] sm:mt-12 px-4">
                <p className="text-gray-500 text-xs sm:text-sm md:text-base text-center mb-4 sm:mb-6">
                  {isFullDeckMode
                    ? `Select ${requiredSelections} cards (${selectedIndices.size}/${requiredSelections} selected)`
                    : isMajorArcanaMode
                      ? `Select ${requiredSelections} cards (${selectedIndices.size}/${requiredSelections} selected)`
                      : "Please choose 1 tarot card."}
                </p>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-6 w-full sm:w-auto">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={shuffleCards}
                    disabled={
                      animationMode !== "idle" || isLoading || !showCards
                    }
                    className={`w-full sm:w-auto px-6 sm:px-8 py-2 sm:py-3 rounded-full text-sm sm:text-base
                    bg-gradient-to-r from-[#4A1F5E] to-[#2C1039]
                    text-white font-medium
                    shadow-md
                    transition-all duration-300
                    ${
                      animationMode !== "idle" || isLoading || !showCards
                        ? "opacity-40 cursor-not-allowed"
                        : "hover:shadow-lg"
                    }`}
                  >
                    ✨ Shuffle
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={cutCards}
                    disabled={
                      animationMode !== "idle" || isLoading || !showCards
                    }
                    className={`w-full sm:w-auto px-6 sm:px-8 py-2 sm:py-3 rounded-full text-sm sm:text-base
                    bg-gradient-to-r from-[#1E0C25] to-[#4A1F5E]
                    text-white font-medium
                    shadow-md
                    transition-all duration-300
                    ${
                      animationMode !== "idle" || isLoading || !showCards
                        ? "opacity-40 cursor-not-allowed"
                        : "hover:shadow-lg"
                    }`}
                  >
                    🔮 Cut
                  </motion.button>
                </div>

                <motion.button
                  whileHover={
                    selectedIndices.size === requiredSelections && !isLoading
                      ? { scale: 1.05 }
                      : {}
                  }
                  whileTap={
                    selectedIndices.size === requiredSelections && !isLoading
                      ? { scale: 0.96 }
                      : {}
                  }
                  disabled={
                    selectedIndices.size !== requiredSelections ||
                    isLoading ||
                    !showCards
                  }
                  className={`w-full sm:w-auto mt-4 sm:mt-6 mb-5 px-8 sm:px-10 py-2 sm:py-3 rounded-full text-sm sm:text-base
                  bg-gradient-to-r from-[#B38EDB] to-[#7E5AA6]
                  text-white font-semibold
                  transition-all duration-300
                  flex items-center justify-center gap-1 sm:gap-2
                  ${
                    selectedIndices.size !== requiredSelections ||
                    isLoading ||
                    !showCards
                      ? "opacity-40 cursor-not-allowed"
                      : "hover:shadow-xl"
                  }`}
                  onClick={handleGetReading}
                >
                  {isFullDeckMode
                    ? `Get Reading (${selectedIndices.size}/${requiredSelections})`
                    : isMajorArcanaMode
                      ? `Get Reading (${selectedIndices.size}/${requiredSelections})`
                      : "Get Your Tarot Reading"}
                </motion.button>
              </div>
            ) : (
              <div
                className={`absolute flex flex-col items-center w-full px-2 
  ${isMobile ? "top-[100%] mt-20" : "top-1/2 -translate-y-1/2"}`}
              >
                <p className="text-gray-500 text-xs sm:text-sm md:text-base text-center mb-4 sm:mb-6">
                  {isFullDeckMode
                    ? `${t("tarotFanSpread.select")} ${requiredSelections} ${t("tarotFanSpread.cards")} (${selectedIndices.size}/${requiredSelections} ${t("tarotFanSpread.selected")})`
                    : isMajorArcanaMode
                      ? `${t("tarotFanSpread.select")} ${requiredSelections} ${t("tarotFanSpread.cards")} (${selectedIndices.size}/${requiredSelections} ${t("tarotFanSpread.selected")})`
                      : t("tarotFanSpread.common")}
                </p>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-6 w-full sm:w-auto px-4 sm:px-0">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={shuffleCards}
                    disabled={
                      animationMode !== "idle" || isLoading || !showCards
                    }
                    className={`w-full sm:w-auto px-6 sm:px-8 py-2 sm:py-3 rounded-full text-sm sm:text-base
        bg-gradient-to-r from-[#4A1F5E] to-[#2C1039]
        text-white font-medium
        shadow-md
        transition-all duration-300
        ${
          animationMode !== "idle" || isLoading || !showCards
            ? "opacity-40 cursor-not-allowed"
            : "hover:shadow-lg"
        }`}
                  >
                    ✨ {t("tarotFanSpread.shuffleButton")}
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={cutCards}
                    disabled={
                      animationMode !== "idle" || isLoading || !showCards
                    }
                    className={`w-full sm:w-auto px-6 sm:px-8 py-2 sm:py-3 rounded-full text-sm sm:text-base
        bg-gradient-to-r from-[#1E0C25] to-[#4A1F5E]
        text-white font-medium
        shadow-md
        transition-all duration-300
        ${
          animationMode !== "idle" || isLoading || !showCards
            ? "opacity-40 cursor-not-allowed"
            : "hover:shadow-lg"
        }`}
                  >
                    🔮 {t("tarotFanSpread.cutButton")}
                  </motion.button>
                </div>

                <motion.button
                  whileHover={
                    selectedIndices.size === requiredSelections && !isLoading
                      ? { scale: 1.05 }
                      : {}
                  }
                  whileTap={
                    selectedIndices.size === requiredSelections && !isLoading
                      ? { scale: 0.96 }
                      : {}
                  }
                  disabled={
                    selectedIndices.size !== requiredSelections ||
                    isLoading ||
                    !showCards
                  }
                  className={`w-full sm:w-auto mt-3 mb-5 sm:mt-4 px-8 sm:px-10 py-2 sm:py-3 rounded-full text-sm sm:text-base
      bg-gradient-to-r from-[#B38EDB] to-[#7E5AA6]
      text-white font-semibold
      transition-all duration-300
      flex items-center justify-center gap-1 sm:gap-2
      ${
        selectedIndices.size !== requiredSelections || isLoading || !showCards
          ? "opacity-40 cursor-not-allowed"
          : "hover:shadow-xl"
      }`}
                  onClick={handleGetReading}
                >
                  {isFullDeckMode
                    ? `${t("tarotFanSpread.readingButton")} (${selectedIndices.size}/${requiredSelections})`
                    : isMajorArcanaMode
                      ? `${t("tarotFanSpread.readingButton")} (${selectedIndices.size}/${requiredSelections})`
                      : t("tarotFanSpread.readingCommonButton")}
                </motion.button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

// Memoize the main component
export default memo(TarotFanSpread, (prevProps, nextProps) => {
  return (
    prevProps.category === nextProps.category &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.theme === nextProps.theme
  );
});
